import React, { useState, useImperativeHandle, forwardRef } from 'react';
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  SignUpCommand
} from '@aws-sdk/client-cognito-identity-provider';

const AdminAuth = forwardRef(({ onSuccess, onCancel, tenantId }, ref) => {
  const [step, setStep] = useState(1); // 1: phone, 2: otp
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [session, setSession] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // These should come from environment variables
  const COGNITO_REGION = import.meta.env.VITE_COGNITO_REGION || 'us-east-1';
  const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
  
  // Debug logging
  console.log('🔑 Cognito Config:', {
    region: COGNITO_REGION,
    clientId: COGNITO_CLIENT_ID ? `${COGNITO_CLIENT_ID.substring(0, 4)}...` : 'NOT SET'
  });

  // Generate random password for new users
  const randomPassword = () => Math.random().toString(36).slice(-8) + 'Aa1!';

  // Expose methods to parent/voice assistant
  useImperativeHandle(ref, () => ({
    setPhone: (phoneNumber) => {
      console.log('🔧 AdminAuth: Setting phone via voice:', phoneNumber);
      setPhone(phoneNumber);
    },
    setCode: (otpCode) => {
      console.log('🔧 AdminAuth: Setting code via voice:', otpCode);
      setCode(otpCode);
    },
    sendOtp: async () => {
      console.log('🔧 AdminAuth: sendOtp called via voice');
      console.log('🔧 Current phone value:', phone);
      console.log('🔧 Current step:', step);
      try {
        const result = await sendOtp();
        console.log('🔧 sendOtp completed');
        return result;
      } catch (error) {
        console.error('❌ sendOtp error:', error);
        throw error;
      }
    },
    verifyOtp: async () => {
      console.log('🔧 AdminAuth: verifyOtp called via voice');
      console.log('🔧 Current code value:', code);
      console.log('🔧 Current step:', step);
      try {
        const result = await verifyOtp();
        console.log('🔧 verifyOtp completed');
        return result;
      } catch (error) {
        console.error('❌ verifyOtp error:', error);
        throw error;
      }
    },
    getCurrentStep: () => step,
    getFormData: () => ({
      step,
      phone,
      code,
      loading,
      message
    })
  }), [step, phone, code, loading, message]);

  const sendOtp = async () => {
    console.log('📤 sendOtp called');
    console.log('📤 Phone value:', phone);
    console.log('📤 Phone trimmed:', phone.trim());
    
    if (!phone.trim()) {
      console.error('❌ No phone number provided');
      setMessage('Please enter a phone number');
      return;
    }
    
    // Notify backend that phone is being submitted
    if (window.webSocketRealtimeClient && window.webSocketRealtimeClient.sendEvent) {
      console.log('📤 Sending phone_submitted event to backend');
      window.webSocketRealtimeClient.sendEvent({
        type: 'auth.event',
        event_type: 'phone_submitted',  // Changed from 'event' to 'event_type'
        data: { phone_number: phone }
      });
    }
    
    if (!COGNITO_CLIENT_ID) {
      console.error('❌ COGNITO_CLIENT_ID not set');
      setMessage('❌ Authentication service not configured properly');
      console.error('🔑 VITE_COGNITO_CLIENT_ID environment variable is not set');
      return;
    }

    console.log('📤 Starting OTP send process...');
    setLoading(true);
    setMessage('');
    
    const client = new CognitoIdentityProviderClient({
      region: COGNITO_REGION
    });

    try {
      let resp = await client.send(
        new InitiateAuthCommand({
          ClientId: COGNITO_CLIENT_ID,
          AuthFlow: 'CUSTOM_AUTH',
          AuthParameters: { USERNAME: phone }
        })
      );
      
      setSession(resp.Session);
      setStep(2);
      setMessage('✅ OTP sent! Check your phone.');
    } catch (err) {
      // Auto-signup if user doesn't exist
      const msg = err.message || '';
      if (
        err.name === 'UserNotFoundException' ||
        err.name === 'NotAuthorizedException' ||
        msg.includes('No phone_number attribute on user') ||
        msg.includes('CreateAuthChallenge failed')
      ) {
        try {
          await client.send(
            new SignUpCommand({
              ClientId: COGNITO_CLIENT_ID,
              Username: phone,
              Password: randomPassword(),
              UserAttributes: [{ Name: 'phone_number', Value: phone }]
            })
          );
          
          // Retry sending OTP
          const resp = await client.send(
            new InitiateAuthCommand({
              ClientId: COGNITO_CLIENT_ID,
              AuthFlow: 'CUSTOM_AUTH',
              AuthParameters: { USERNAME: phone }
            })
          );
          
          setSession(resp.Session);
          setStep(2);
          setMessage('✅ OTP sent! Check your phone.');
        } catch (signErr) {
          console.error(signErr);
          setMessage('❌ ' + signErr.message);
        }
      } else {
        console.error(err);
        setMessage('❌ ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!code.trim() || code.length !== 6) {
      setMessage('Please enter a valid 6-digit code');
      return;
    }
    
    // Notify backend that OTP is being submitted
    if (window.webSocketRealtimeClient && window.webSocketRealtimeClient.sendEvent) {
      console.log('📤 Sending otp_submitted event to backend');
      window.webSocketRealtimeClient.sendEvent({
        type: 'auth.event',
        event_type: 'otp_submitted',  // Changed from 'event' to 'event_type'
        data: { otp_code: code }
      });
    }

    setLoading(true);
    setMessage('');

    try {
      const client = new CognitoIdentityProviderClient({
        region: COGNITO_REGION
      });

      const response = await client.send(
        new RespondToAuthChallengeCommand({
          ClientId: COGNITO_CLIENT_ID,
          ChallengeName: 'CUSTOM_CHALLENGE',
          Session: session,
          ChallengeResponses: {
            USERNAME: phone,
            ANSWER: code
          }
        })
      );

      // Store authentication data
      const authData = {
        accessToken: response.AuthenticationResult.AccessToken,
        idToken: response.AuthenticationResult.IdToken,
        refreshToken: response.AuthenticationResult.RefreshToken,
        phone: phone,
        tenantId: tenantId,
        timestamp: Date.now()
      };

      localStorage.setItem('adminAuth', JSON.stringify(authData));
      setMessage('✅ Authentication successful!');
      
      // Notify backend of successful authentication BEFORE calling onSuccess
      if (window.webSocketRealtimeClient && window.webSocketRealtimeClient.sendEvent) {
        console.log('📤 Sending auth_success event to backend with authData:', authData);
        const sent = window.webSocketRealtimeClient.sendEvent({
          type: 'auth.event',
          event_type: 'auth_success',
          data: authData
        });
        console.log('📤 Auth success event sent result:', sent);
      } else {
        console.error('❌ Cannot send auth_success event - webSocketRealtimeClient not available');
        console.log('window.webSocketRealtimeClient:', window.webSocketRealtimeClient);
      }
      
      // Call success callback with auth data
      setTimeout(() => onSuccess(authData), 1000);
      
    } catch (err) {
      console.error(err);
      setMessage('❌ Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const overlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001, // Higher than video overlay
    padding: '20px',
    boxSizing: 'border-box'
  };

  const modalStyles = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
    position: 'relative'
  };

  const buttonStyles = {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    marginRight: '10px',
    opacity: loading ? 0.6 : 1
  };

  const inputStyles = {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    marginBottom: '15px',
    textAlign: step === 2 ? 'center' : 'left',
    letterSpacing: step === 2 ? '2px' : 'normal'
  };

  return (
    <div style={overlayStyles}>
      <div style={modalStyles}>
        <button
          onClick={onCancel}
          style={{
            position: 'absolute',
            top: '10px',
            right: '15px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666'
          }}
        >
          ×
        </button>

        <h2 style={{ marginBottom: '20px', color: '#333' }}>
          Admin Authentication
        </h2>

        {step === 1 ? (
          <>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Enter your phone number to receive a verification code
            </p>
            <input
              type="tel"
              placeholder="+15551234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={inputStyles}
              disabled={loading}
            />
            <div>
              <button
                onClick={sendOtp}
                disabled={loading || !phone.trim()}
                style={buttonStyles}
              >
                {loading ? 'Sending...' : 'Send Code'}
              </button>
              <button
                onClick={onCancel}
                style={{
                  ...buttonStyles,
                  backgroundColor: '#6c757d'
                }}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Enter the 6-digit code sent to:<br />
              <strong>{phone}</strong>
            </p>
            <input
              type="text"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={inputStyles}
              maxLength={6}
              disabled={loading}
            />
            <div>
              <button
                onClick={() => {
                  setStep(1);
                  setCode('');
                  setMessage('');
                }}
                style={{
                  ...buttonStyles,
                  backgroundColor: '#6c757d'
                }}
              >
                ← Back
              </button>
              <button
                onClick={verifyOtp}
                disabled={loading || code.length !== 6}
                style={buttonStyles}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </>
        )}

        {message && (
          <div style={{
            marginTop: '20px',
            padding: '10px',
            borderRadius: '6px',
            backgroundColor: message.includes('❌') ? '#f8d7da' : '#d4edda',
            color: message.includes('❌') ? '#721c24' : '#155724',
            border: message.includes('❌') ? '1px solid #f5c6cb' : '1px solid #c3e6cb'
          }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
});

export default AdminAuth;